/**
 * PILOT-25: TriageList
 *
 * Scrollable list of all open guest requests, sorted by urgency.
 * Rendered as the primary content area of the Control Tower Triage Hub.
 *
 * Empty state: "All caught up — no open requests" with a timestamp.
 */

import { useState } from 'react';
import type { TriageItem } from '../../services/controlTowerTypes';
import { TriageRow } from './TriageRow';

interface TriageListProps {
  items: readonly TriageItem[];
  computedAt: string;
}

export function TriageList({ items, computedAt }: TriageListProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const breachedCount = items.filter(i => i.slaBreached).length;
  const escalatedCount = items.filter(i => i.status === 'ESCALATED').length;

  return (
    <div className="bg-slate-800/60 border border-slate-700/60 rounded-xl overflow-hidden">
      {/* Header */}
      <button
        type="button"
        onClick={() => setIsExpanded(e => !e)}
        className="w-full flex items-center justify-between px-4 py-3 border-b border-slate-700/60 hover:bg-slate-700/30 transition-colors focus:outline-none"
      >
        <div className="flex items-center gap-2">
          <svg
            className={`w-3.5 h-3.5 text-slate-500 transition-transform duration-150 ${isExpanded ? 'rotate-90' : ''}`}
            fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <h2 className="text-sm font-semibold text-slate-100">Open Requests</h2>
          {items.length > 0 && (
            <span className="px-1.5 py-0.5 rounded text-[11px] font-medium bg-slate-700 text-slate-300 tabular-nums">
              {items.length}
            </span>
          )}
          {escalatedCount > 0 && (
            <span className="px-1.5 py-0.5 rounded text-[11px] font-medium bg-rose-500/20 text-rose-300 border border-rose-500/20">
              {escalatedCount} escalated
            </span>
          )}
          {breachedCount > 0 && (
            <span className="px-1.5 py-0.5 rounded text-[11px] font-medium bg-rose-500/10 text-rose-400 border border-rose-500/20">
              {breachedCount} SLA breached
            </span>
          )}
        </div>
        <span className="text-[11px] text-slate-500">
          Updated {new Date(computedAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
        </span>
      </button>

      {/* Collapsible body */}
      {isExpanded && (<>

      {/* Column headers */}
      {items.length > 0 && (
        <div className="flex items-center gap-3 px-4 py-1.5 border-b border-slate-700/40 bg-slate-800/40">
          <span className="w-14 shrink-0 text-[10px] uppercase tracking-wider text-slate-600">Room</span>
          <span className="w-32 shrink-0 text-[10px] uppercase tracking-wider text-slate-600">Guest</span>
          <span className="w-32 shrink-0 text-[10px] uppercase tracking-wider text-slate-600">Category</span>
          <span className="flex-1 text-[10px] uppercase tracking-wider text-slate-600">Description</span>
          <span className="shrink-0 w-20 text-[10px] uppercase tracking-wider text-slate-600">Status</span>
          <span className="w-16 shrink-0 text-right text-[10px] uppercase tracking-wider text-slate-600">Age</span>
          <span className="w-40 shrink-0 text-right text-[10px] uppercase tracking-wider text-slate-600">Assignee</span>
          <span className="w-4 shrink-0" aria-hidden="true" />
        </div>
      )}

      {/* Rows or empty state */}
      {items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-14 gap-2">
          <svg
            className="w-8 h-8 text-emerald-500/50"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-sm font-medium text-slate-400">All caught up — no open requests</p>
          <p className="text-[11px] text-slate-600">
            As of {new Date(computedAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
          </p>
        </div>
      ) : (
        <div className="divide-y divide-slate-700/40 max-h-[600px] overflow-y-auto">
          {items.map(item => (
            <TriageRow key={item.id} item={item} />
          ))}
        </div>
      )}

      </>)}
    </div>
  );
}
